import styles from './AboutUs.module.css'
import { Helmet } from 'react-helmet'

import AboutUsImage from '../../assets/luxury-titanium-wrist-watch-executive-lifestyle.png'

export default function AboutUs() {
  return (
    <div className="main">
      <Helmet>
        <title>Tangible Milestones: Why Modern Founders Invest in Luxury Timepieces | Crifty</title>
        {/*-- SEO Meta Tags --*/}
        <meta name="description" content="Explore how successful entrepreneurs track milestones through asset investments, balancing digital wealth with mechanical precision." />
        <meta name="keywords" content="about crifty, bookkeeping story, small business finance mission, expense tracking app, financial clarity" />
        <meta name="author" content="Crifty" />
        <link rel="canonical" href="https://crifty.com/insights" />
        
        {/* Open Graph / Social Media Meta Tags */}
        <meta property="og:title" content="Tangible Milestones: Why Modern Founders Invest in Luxury Timepieces" />
        <meta property="og:description" content="Explore how successful entrepreneurs track milestones through asset investments, balancing digital wealth with mechanical precision." />
        <meta property="og:image:alt" content="About Crifty Bookkeeping" />
        <meta property="og:url" content="https://crifty.com/insights" />
        <meta property="og:site_name" content="Crifty" /> {/* Keep this consistent across all pages */}
        <meta property="og:type" content="website" /> {/* Keep this consistent across all pages */}
        <meta property="og:image" content="https://crifty.com/OG-crifty.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/*-- Twitter Meta Tags --*/}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tangible Milestones: Why Founders Invest in Luxury Timepieces | Crifty" />
        <meta name="twitter:description" content="Explore how successful entrepreneurs track milestones through asset investments, balancing digital wealth with mechanical precision." />
        <meta name="twitter:image" content="https://crifty.com/OG-crifty.webp" />
        <meta name="twitter:site" content="@CriftyApp" /> 

         <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "AboutPage",
              "name": "About Crifty Bookkeeping",
              "description": "Learn the story behind Crifty Bookkeeping. We built this app after experiencing the chaos of small business finances firsthand.",
              "url": "https://crifty.com/aboutus",
              "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://crifty.com"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "About Us"
                  }
                ]
              },
              "publisher": {
                "@type": "Organization",
                "name": "Crifty",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://crifty.com/logo.png"
                }
              }
            }
          `}
        </script>
        
      </Helmet>
      
      <div className={styles['aboutus-container']}>
        <h1>Tangible Milestones: Why Modern Founders Invest in Luxury Timepieces</h1>
        <img 
          src={AboutUsImage}
          alt='A premium luxury titanium automatic wrist watch resting on an executive desk overlooking a city skyline.'
          width={512}
          height={512}
          // Modern attributes:
          loading="eager"// Eager load first image
          decoding="async" // Non-blocking image decode
          fetchPriority="high"
          className={styles['image']}
        />


        <div className="paragraph">      
          
          <p>In the fast-paced ecosystem of modern entrepreneurship, success is heavily 
						measured through digital data points. Founders and executives live in a world 
						governed by real-time balance sheets, monthly recurring revenue (MRR), capital 
						efficiency, and optimized cash flows.While keeping a pristine digital ledger is 
						critical for tracking a company's financial health, many business leaders find a 
						disconnect between their abstract corporate achievements and their physical 
						everyday realities. When a startup crosses a major revenue threshold, secures a 
						competitive funding round, or executes a successful corporate exit, celebrating 
						that milestone requires something more permanent than numbers on a screen. </p>
					<p>This realization is driving a significant shift in executive lifestyle choices: 
						modern entrepreneurs are increasingly marking their professional breakthrough moments 
						by investing in premium physical assets. Chief among these choices is the acquisition 
						of a classic mechanical timepiece.</p>

          <h2>The Psychology of the Milestone Asset</h2>
          <p>For a busy executive, a high-end timepiece is far more than a tool to tell time—it 
						is a physical anchor of a major business breakthrough. In a world where smartphones 
						constantly pull attention away with disruptive notifications, looking down at your 
						wrist offers a moment of deliberate focus. It serves as a subtle, constant reminder 
						of the grit, discipline, and sleepless nights required to build a thriving enterprise.</p>
          <p>Furthermore, luxury watches operate under the exact same principles as an elite 
						business model: maximum efficiency, high-grade components, and long-term asset 
						retention. A finely tuned enterprise relies on every department moving in perfect 
						harmony; similarly, a premium watch depends on micro-engineering where every 
						component operates with flawless accuracy.</p>

					<h2>Balancing Wealth with Mechanical Utility</h2>
          <p>When building a personal asset collection, seasoned business owners look for two strict 
						criteria: industrial durability and timeless style. While traditional fashion accessories 
						depreciate rapidly, an investment-grade watch retains value through its engineering 
						integrity and high-grade materials.</p>
					<p>This focus on resilient engineering is highly visible in modern micro-horology. 
						Independent watch brands are increasingly pushing past standard designs to deliver 
						exceptional quality for collectors. For instance, the new aerospace-grade 
						<a href="https://valbax.com"> titanium wrist watch </a>
						 collections hitting the premium market have caught the attention of corporate 
						executives who value both lightweight comfort and extreme structural strength.</p>
					<p>By opting for a self-winding 
						<a href="https://valbax.com/product/aviator-automatic-watch/"> automatic watch </a>
						 mechanism over standard battery-powered 
						variants, founders are investing in an asset built to last for generations. Protected 
						by a virtually scratch-proof sapphire crystal and an incredibly light titanium chassis, 
						these timepieces easily transition from morning board meetings to demanding weekend 
						travel schedules without missing a beat.</p>

          <h2>Tracking Your Personal Balance Sheet</h2>
          <p>As a business owner, your time is your most finite asset. While cloud software manages 
						your corporate accounting, celebrating the personal sacrifices behind those corporate 
						numbers is equally necessary for long-term entrepreneurial drive.</p>

          <p>Whether you are preparing to mark your company's first profitable quarter or celebrating 
						a decade of market disruption, investing in a high-performance timepiece ensures your 
						hardest-earned milestones are beautifully preserved in a tangible, lasting asset.</p>

          
        </div>

        <h4>Founder of Crifty</h4>
      </div>
    </div>
  )
}
